import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Close,
  HelpOutline,
} from "@mui/icons-material";
import React from "react";

interface FieldMapping {
  email?: string[];
  phone?: string[];
  linkedinUrl?: string[];
  address?: string[];
  city?: string[];
  state?: string[];
  zip?: string[];
}

interface ListHeader {
  id: string;
  name: string;
  type?: string;
}

interface FieldMappingSelectorProps {
  fieldMappings: FieldMapping;
  onChange: (mappings: FieldMapping) => void;
  availableHeaders: ListHeader[];
  showHelp?: boolean;
  fieldTypes?: Array<keyof FieldMapping>;
}

const FIELD_CONFIG = {
  email: {
    label: "Email Fields",
    tooltip:
      "Select fields to use as email, in order of preference. B2B fields are prioritized by default.",
    suggestedFields: [
      // B2B fields first (best for outreach)
      "Business Verified Email",
      "businessVerifiedEmail",
      "Business Email",
      "businessEmail",
      // Then primary email field
      "Email",
      "email",
      // Then B2C fields
      "Personal Verified Email",
      "personalVerifiedEmail",
      "Personal Email",
      "personalEmail",
      // Legacy field names
      "work_email",
      "personal_email",
    ],
  },
  phone: {
    label: "Phone Fields",
    tooltip:
      "Select fields to use as phone number, in order of preference. B2B skiptrace fields are prioritized by default for business outreach.",
    suggestedFields: [
      // B2B skiptrace fields first (best for outbound calls)
      "Skiptrace B2B Wireless",
      "skiptraceB2bWireless",
      "Skiptrace B2B Landline",
      "skiptraceB2bLandline",
      // Then direct/work numbers
      "Direct Number",
      "directNumber",
      // Then primary phone field
      "Phone Number",
      "phoneNumber",
      "phone",
      // Then mobile/cell
      "Mobile Phone",
      "mobilePhone",
      "mobile",
      "cell",
      // Then B2C skiptrace fields
      "Skiptrace Wireless Numbers",
      "skiptraceWirelessNumbers",
      "Skiptrace Landline Numbers",
      "skiptraceLandlineNumbers",
      // Then personal phone
      "Personal Phone",
      "personalPhone",
      "personal_phone",
      "telephone",
      "work_phone",
    ],
  },
  linkedinUrl: {
    label: "LinkedIn URL Fields",
    tooltip:
      "Select fields to use as LinkedIn URL, in order of preference. The first non-empty value will be used.",
    suggestedFields: [
      "LinkedIn URL",
      "linkedinUrl",
      "linkedin",
      "linkedInUrl",
      "linkedin_url",
      "linkedin_profile",
    ],
  },
  address: {
    label: "Address Fields",
    tooltip:
      "Select fields to use as street address, in order of preference. Skiptrace address is prioritized for verified delivery.",
    suggestedFields: [
      // Skiptrace address first (verified)
      "Skiptrace Address",
      "skiptraceAddress",
      // Then personal address
      "Personal Address",
      "personalAddress",
      "address",
      "street",
      "address1",
      "streetAddress",
      "Address",
      "Street",
      "street_address",
    ],
  },
  city: {
    label: "City Fields",
    tooltip:
      "Select fields to use as city, in order of preference. Required for direct mail.",
    suggestedFields: [
      "Skiptrace City",
      "skiptraceCity",
      "Personal City",
      "personalCity",
      "city",
      "City",
      "town",
      "locality",
    ],
  },
  state: {
    label: "State Fields",
    tooltip:
      "Select fields to use as state/province, in order of preference. Required for direct mail.",
    suggestedFields: [
      "Skiptrace State",
      "skiptraceState",
      "Personal State",
      "personalState",
      "state",
      "State",
      "province",
      "Province",
      "region",
      "state_province",
    ],
  },
  zip: {
    label: "Zip/Postal Code Fields",
    tooltip:
      "Select fields to use as zip/postal code, in order of preference. Required for direct mail.",
    suggestedFields: [
      "Skiptrace Zip",
      "skiptraceZip",
      "Personal Zip",
      "personalZip",
      "zip",
      "zipCode",
      "postalCode",
      "postal_code",
      "Zip",
      "ZIP",
      "Postal Code",
    ],
  },
};

type FieldType = keyof typeof FIELD_CONFIG;

const DEFAULT_FIELD_TYPES: FieldType[] = [
  "email",
  "phone",
  "linkedinUrl",
  "address",
  "city",
  "state",
  "zip",
];

const FieldMappingSelector: React.FC<FieldMappingSelectorProps> = ({
  fieldMappings,
  onChange,
  availableHeaders,
  showHelp = true,
  fieldTypes,
}) => {
  const typesToRender: FieldType[] =
    (fieldTypes as FieldType[] | undefined) || DEFAULT_FIELD_TYPES;
  const handleFieldChange = (
    fieldType: FieldType,
    event: SelectChangeEvent<string[]>
  ) => {
    const value = event.target.value;
    const selectedFields = typeof value === "string" ? value.split(",") : value;

    onChange({
      ...fieldMappings,
      [fieldType]: selectedFields.length > 0 ? selectedFields : undefined,
    });
  };

  const handleRemoveField = (fieldType: FieldType, fieldName: string) => {
    const currentFields = fieldMappings[fieldType] || [];
    const newFields = currentFields.filter((f) => f !== fieldName);

    onChange({
      ...fieldMappings,
      [fieldType]: newFields.length > 0 ? newFields : undefined,
    });
  };

  const handleMoveField = (
    fieldType: FieldType,
    fieldName: string,
    direction: "up" | "down"
  ) => {
    const currentFields = [...(fieldMappings[fieldType] || [])];
    const index = currentFields.indexOf(fieldName);

    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= currentFields.length) return;

    // Swap positions
    [currentFields[index], currentFields[newIndex]] = [
      currentFields[newIndex],
      currentFields[index],
    ];

    onChange({
      ...fieldMappings,
      [fieldType]: currentFields,
    });
  };

  // Get header names from available headers
  const headerNames = availableHeaders.map((h) => h.name);

  // Filter suggested fields to only include those that exist in headers
  const getAvailableFieldsForType = (fieldType: FieldType): string[] => {
    const suggested = FIELD_CONFIG[fieldType].suggestedFields;
    const availableFromSuggested = suggested.filter((f) =>
      headerNames.includes(f)
    );
    // Also include all headers that aren't already suggested
    const otherHeaders = headerNames.filter((h) => !suggested.includes(h));
    return [...availableFromSuggested, ...otherHeaders];
  };

  const renderFieldSelector = (fieldType: FieldType) => {
    const config = FIELD_CONFIG[fieldType];
    const selectedFields = fieldMappings[fieldType] || [];
    const availableFields = getAvailableFieldsForType(fieldType);

    return (
      <Box key={fieldType} sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          {config.label}
          {showHelp && (
            <Tooltip title={config.tooltip}>
              <HelpOutline
                sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
              />
            </Tooltip>
          )}
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>{config.label}</InputLabel>
          <Select
            multiple
            value={selectedFields}
            onChange={(e) => handleFieldChange(fieldType, e)}
            input={<OutlinedInput label={config.label} />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value, index) => (
                  <Chip
                    key={value}
                    label={`${index + 1}. ${value}`}
                    size="small"
                    onDelete={() => handleRemoveField(fieldType, value)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
              </Box>
            )}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {availableFields.map((field) => (
              <MenuItem
                key={field}
                value={field}
                disabled={selectedFields.includes(field)}
              >
                {field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show selected fields with reorder controls */}
        {selectedFields.length > 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Priority order (drag to reorder):
            </Typography>
            {selectedFields.map((field, index) => (
              <Box
                key={field}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 20, fontWeight: 600 }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {field}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleMoveField(fieldType, field, "up")}
                  disabled={index === 0}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveField(fieldType, field, "down")}
                  disabled={index === selectedFields.length - 1}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveField(fieldType, field)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return <Box>{typesToRender.map((t) => renderFieldSelector(t))}</Box>;
};

export default FieldMappingSelector;
